import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_URL = `${API_URL}/api/v1/auth/refresh`;

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  withCredentials: true, // Required to send the HttpOnly refresh token cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Silent refresh state ---
let isRefreshing = false;
let failedQueue = []; // Holds requests that arrived while a refresh was in-flight

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('student');
  window.location.href = '/login';
};

// Request interceptor — attach current access token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

// Response interceptor — on 401, attempt silent refresh then retry
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Only intercept genuine 401s; skip the refresh endpoint itself to avoid loops
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is already in flight — queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Use raw axios so this call does NOT pass through the interceptor again
      const { data } = await axios.post(
        REFRESH_URL,
        {},
        { withCredentials: true },
      );
      const newToken = data.token;

      localStorage.setItem('token', newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      processQueue(null, newToken);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
